export default function miniTicketCalendar() {
	const calendar = document.querySelector('[farlo-ui="calendar-mini"]');
	if (!calendar) {
		return;
	}

	// ----- payload -----
	let payload;
	try {
		payload = JSON.parse(calendar.getAttribute('data-cal') || '{}') || {};
	} catch {
		payload = {};
	}

	const specialOfferNote = (payload.specialOfferNote || '').toString().trim();
	const exclusiveOfferNote = (payload.exclusiveOfferNote || '').toString().trim();
	const currency = payload.currency || '£';
	const hideDiscountMessaging = boolish(
		payload?.hideDiscountMessaging ?? payload?.hide_discount_messaging,
		false
	);

	/*
	 * Multi-part show price multiplier.
	 *
	 * Multi-part productions require a separate ticket purchase for each part,
	 * so every displayed price is multiplied by the number of parts.
	 * Kids Week prices are further doubled (one adult + one child per part).
	 *
	 * partsCount is supplied by PHP (olt_calendar_payload_for_show) from the
	 * show_multiple_parts and show_multiple_parts_amount ACF fields.
	 */
	const partsCount = (Number.isInteger(payload.partsCount) && payload.partsCount > 0) ? payload.partsCount : 1;

	// ----- Modal refs -----
	const modal = document.querySelector('[data-cal-modal]');
	const modalTitle = document.querySelector('[data-cal-modal-title]');
	const modalContent = document.querySelector('[data-cal-modal-content]');
	const modalClose = document.querySelectorAll('[data-cal-close]');

	// Campaign access
	const access = payload.campaignAccess || {
		brand: '',
		eligible: false,
		publicOpen: false,
		priorityOpen: false,
		priorityIso: null,
		revealIso: null,
		canBookNow: false,
		finished: false
	};

	const campaignBrandActive = !!(access.brand && !access.finished);
	const campaignUIOn = campaignBrandActive;

	// Access performance dates keyed by YYYY-MM-DD, sourced from the access_performances CPT.
	// Each value is an array of type slugs: 'ad', 'cap', 'bsl', 'relaxed'.
	const accessDates = (payload.accessDates && typeof payload.accessDates === 'object')
		? payload.accessDates
		: {};

	// Maps each type slug to its CSS modifier class and accessible label text.
	const ACCESS_ICON_MAP = {
		ad: { mod: 'ad', label: 'Audio Described' },
		cap: { mod: 'cap', label: 'Captioned' },
		bsl: { mod: 'bsl', label: 'British Sign Language' },
		relaxed: { mod: 'relaxed', label: 'Relaxed' }
	};

	/**
	 * Returns an HTML string of access performance type icons for a specific date
	 * and time slot. Checks both the exact time key and the 'any' key so that
	 * access performances recorded without a specific time still appear on every slot.
	 *
	 * @param {string} date - YYYY-MM-DD date string.
	 * @param {string} time - HH:MM 24-hour time string (from performance data).
	 * @return {string}
	 */
	function accessIconsForSlot(date, time) {
		const dateEntry = accessDates[ date ];

		if (!dateEntry || typeof dateEntry !== 'object') {
			return '';
		}

		const types = new Set([
			...(dateEntry[ time ] || []),
			...(dateEntry.any || [])
		]);

		if (!types.size) {
			return '';
		}

		const icons = [...types].map((slug) => {
			const info = ACCESS_ICON_MAP[ slug ];

			if (!info) {
				return '';
			}

			return `<span class="farlo-ui-ticket-calendar__access-icon farlo-ui-ticket-calendar__access-icon--${ info.mod }" role="img" aria-label="${ info.label }"><span class="farlo-ui-ticket-calendar__access-icon-label">${ info.label }</span></span>`;
		}).filter(Boolean).join('');

		if (!icons) {
			return '';
		}

		return `<span class="farlo-ui-ticket-calendar__access-icons" aria-label="Access performance">${ icons }</span>`;
	}

	const isCampaignLabel = (l) => /kids/i.test(l) || /new\s*year'?s?\s*sale/i.test(l);
	const visibleLabels = (labels = []) => campaignBrandActive ? labels.slice() : labels.filter((l) => !isCampaignLabel(String(l)));

	const isCampaignPerf = (perf) => {
		if (!campaignBrandActive) {
			return false;
		}
		const labs = (perf.labels || []).map((x) => String(x).toLowerCase());
		if (access.brand === 'kw') {
			return labs.some((l) => l.includes('kids'));
		}
		if (access.brand === 'nys' || access.brand === 'gilt') {
			return labs.some((l) => /new\s*year'?s?\s*sale/.test(l));
		}
		return false;
	};

	const LDN_TZ = 'Europe/London';
	const fmtLDN = (d, opts) => d.toLocaleString(undefined, { timeZone: LDN_TZ, ...opts });

	function boolish(v, def = true) {
		if (v === undefined || v === null) {
			return def;
		}
		if (typeof v === 'boolean') {
			return v;
		}
		if (typeof v === 'number') {
			return v !== 0;
		}
		const s = String(v).trim().toLowerCase();
		if (['', '0', 'false', 'no', 'off', 'null'].includes(s)) {
			return false;
		}
		if (['1', 'true', 'yes', 'on'].includes(s)) {
			return true;
		}
		return def;
	}

	const isOlt = (p) =>
		(String(p.source || '').toLowerCase() === 'olt') ||
  ((p.labels || []).some((l) => /^olt$/i.test(String(l))));

	// unified “has a real discount?” check
	function hasAnyDiscount(p) {
		const pct = p.savePct ?? p.discount_pct ?? p.discountPercent ?? p.savingPercent;
		if (typeof pct === 'number' && pct > 0) {
			return true;
		}
		const was = Number(p.priceWas ?? p.was ?? p.original_ticket_price ?? p.original_face_value ?? 0);
		const price = Number(p.price ?? p.min ?? p.ticket_price ?? 0);
		return was > 0 && price > 0 && price < was;
	}

	const disableFutureDeals = boolish(
		payload?.showDisableFutureDeals ?? payload?.show_disable_future_deals,
		true
	);
	const todayKeyLDN = (() => {
		const d = new Date();
		const y = fmtLDN(d, { year: 'numeric' });
		const m = fmtLDN(d, { month: '2-digit' });
		const dd = fmtLDN(d, { day: '2-digit' });
		return `${ y }-${ m }-${ dd }`;
	})();

	const isOfferPerf = (p) => {
		if (isOlt(p)) {
			return false;
		}
		if (isCampaignPerf(p)) {
			return false;
		}
		return true;
	};
	const isOfferAllowed = (p) => {
		if (!disableFutureDeals) {
			return true;
		}
		if (!isOfferPerf(p)) {
			return true;
		} // only clamp exclusive/special
		return String(p.date) === todayKeyLDN;
	};

	const parseIsoMs = (iso) => {
		const ms = Date.parse(iso || '');
		return Number.isFinite(ms) ? ms : null;
	};

	const revealMs = parseIsoMs(access.revealIso);
	const prioMs = parseIsoMs(access.priorityIso);
	const eligible = access.eligible === true;

	// Check for campaign cookie client-side (bypasses page cache)
	const hasCampaignCookie = () => document.cookie.includes('farlo_campaign_access');

	function userOpenMs() {
		// Check cookie client-side instead of cached eligible flag
		if (hasCampaignCookie() && prioMs !== null) {
			return prioMs;
		}
		return revealMs;
	}

	// Single source of truth for UI lock/unlock
	function canBookNow() {
		const openAt = userOpenMs();
		if (openAt === null) {
			return false;
		}
		return Date.now() >= openAt;
	}

	// Message shown when locked
	function onSaleMsg() {
		const tMs = userOpenMs();
		if (tMs === null) {
			return 'On sale soon';
		}
		const t = new Date(tMs);
		const sameDay =
        fmtLDN(t, { year: 'numeric', month: '2-digit', day: '2-digit' }) ===
        fmtLDN(new Date(), { year: 'numeric', month: '2-digit', day: '2-digit' });
		const timeStr = fmtLDN(t, { hour: 'numeric', minute: '2-digit', hour12: true })
			.replace(':00', '')
			.replace(' ', '')
			.toLowerCase();
		if (sameDay) {
			return `On sale at ${ timeStr }`;
		}
		const dateStr = fmtLDN(t, { day: 'numeric', month: 'long' });
		return `On sale on ${ dateStr } at ${ timeStr }`;
	}

	function perfTimestamp(p) {
		const iso = p.iso || `${ p.date }T${ (p.time || '00:00') }:00`;
		const ms = Date.parse(iso);
		return Number.isNaN(ms) ? null : ms;
	}
	function isPastPerf(p) {
		const ms = perfTimestamp(p);
		const cutoff = Date.now() + (60 * 60 * 1000);
		return ms !== null && ms < cutoff;
	}

	const headerEl = calendar.querySelector('.farlo-ui-ticket-calendar__header-month-year');
	const daysRoot = calendar.querySelector('.farlo-ui-ticket-calendar__days');
	const prevBtn = calendar.querySelector('.farlo-ui-ticket-calendar__button--prev');
	const nextBtn = calendar.querySelector('.farlo-ui-ticket-calendar__button--next');
	const filterWrap = calendar.querySelector('.farlo-ui-ticket-calendar__filters');
	const filterBtn = filterWrap?.querySelector('.farlo-ui-ticket-calendar__filter-toggle');
	const filterPop = filterWrap?.querySelector('.farlo-ui-ticket-calendar__filter-popover');
	const filterListEl = filterWrap?.querySelector('[data-filter-list]');
	const filterClear = filterWrap?.querySelector('.farlo-ui-ticket-calendar__filter-clear');

	const pad2 = (n) => n < 10 ? '0' + n : '' + n;
	const parseYMD = (s) => {
		const [y, m, d] = (s || '').split('-').map((n) => parseInt(n, 10)); return new Date(y, m - 1, d);
	};

	const perfsAll = Array.isArray(payload.performances) ? payload.performances : [];
	const perfDates = perfsAll.filter((p) => (p.avail || 0) > 0 && p.date).map((p) => p.date);
	const dateKeys = perfDates.length ? perfDates : Object.keys(payload.dates || {});
	dateKeys.sort();

	const minDate = dateKeys[ 0 ] ? parseYMD(dateKeys[ 0 ]) : null;
	const maxDate = dateKeys.length ? parseYMD(dateKeys[ dateKeys.length - 1 ]) : null;

	const monthIndex = (y, m) => y * 12 + m;

	let detailsRow = null;
	function ensureDetailsRow() {
		if (detailsRow) {
			detailsRow.remove();
		}
		detailsRow = document.createElement('div');
		detailsRow.className = 'farlo-ui-ticket-calendar__details';
		return detailsRow;
	}

	const firstPerf = perfDates.length ? parseYMD(perfDates[ 0 ]) : null;
	const now = new Date();
	let viewYear = firstPerf ? firstPerf.getFullYear() : now.getFullYear();
	let viewMonth = firstPerf ? firstPerf.getMonth() : now.getMonth();
	if (minDate && (viewYear < minDate.getFullYear() || (viewYear === minDate.getFullYear() && viewMonth < minDate.getMonth()))) {
		viewYear = minDate.getFullYear(); viewMonth = minDate.getMonth();
	}
	if (maxDate && (viewYear > maxDate.getFullYear() || (viewYear === maxDate.getFullYear() && viewMonth > maxDate.getMonth()))) {
		viewYear = maxDate.getFullYear(); viewMonth = maxDate.getMonth();
	}

	const urlDate = new URLSearchParams(window.location.search).get('date');
	const isValidYmd = (s) => /^\d{4}-\d{2}-\d{2}$/.test(s);
	let deepLinkKey = null;

	if (isValidYmd(urlDate)) {
		const d = parseYMD(urlDate);
		// Clamp into [minDate, maxDate] bounds, then set view to that month
		if (d && (!minDate || d >= minDate) && (!maxDate || d <= maxDate)) {
			viewYear = d.getFullYear();
			viewMonth = d.getMonth();
			deepLinkKey = urlDate;
		}
	}

	let activeDateKey = null;

	const ymToLabel = (y, m) => new Date(Date.UTC(y, m, 1)).toLocaleString(undefined, { month: 'long', year: 'numeric', timeZone: LDN_TZ });
	const dateKey = (y, m, d) => `${ y }-${ pad2(m + 1) }-${ pad2(d) }`;
	const firstDow = (y, m) => (new Date(y, m, 1).getDay() + 6) % 7;
	const daysInMon = (y, m) => new Date(y, m + 1, 0).getDate();

	const labelUniverse = (() => {
		const set = new Set();
		perfsAll.forEach((p) => visibleLabels(p.labels || []).forEach((l) => set.add(l)));
		const wanted = ['Special Offer', 'Kids Week', 'New Year Sale', 'New Years Sale'];
		const foundRaw = wanted.filter((w) => [...set].some((l) => String(l).toLowerCase() === w.toLowerCase()));
		const found = campaignUIOn ? foundRaw : foundRaw.filter((w) => !/kids|new\s*year/.test(w.toLowerCase()));
		const rest = [...set].filter((l) => !found.some((f) => f.toLowerCase() === String(l).toLowerCase()));
		return [...found, ...rest];
	})();

	const sessionOf = (timeStr) => {
		const [hh, mm] = (timeStr || '').split(':').map((n) => parseInt(n, 10) || 0);
		return (hh > 17 || (hh === 17 && mm >= 30)) ? 'Evening' : 'Matinee';
	};

	const active = { labels: new Set(), sessions: new Set(), offerFilter: null };
	const anyActive = () => active.labels.size || active.sessions.size || active.offerFilter;

	function matchFilters(perf) {
		if (active.offerFilter === 'special' && !(hasAnyDiscount(perf) && !isCampaignPerf(perf))) {
			return false;
		}

		if (active.offerFilter === 'exclusive' && (isOlt(perf) || isCampaignPerf(perf) || hasAnyDiscount(perf))) {
			return false;
		}
		if (active.labels.size) {
			const labels = (perf.labels || []).map(String);
			if (!labels.some((l) => active.labels.has(l))) {
				return false;
			}
		}
		if (active.sessions.size) {
			if (!active.sessions.has(sessionOf(perf.time))) {
				return false;
			}
		}
		return true;
	}
	function filteredPerfsForDate(key) {
		return perfsAll
			.filter((p) => p.date === key && (p.avail || 0) > 0)
			.filter((p) => !isPastPerf(p))
			.filter(isOfferAllowed)
			.filter(matchFilters);
	}
	function filteredHasDate(key) {
		return filteredPerfsForDate(key).length > 0;
	}

	function isValidPrice(v) {
		return typeof v === 'number' && isFinite(v) && v > 0;
	}
	function formatMoney(v, cur = '£') {
		if (!isValidPrice(v)) {
			return '';
		} const cents = Math.round((v + Number.EPSILON) * 100); const exact = cents / 100; return cur + (Number.isInteger(exact) ? String(exact) : exact.toFixed(2));
	}
	function filteredMinForDate(key) {
		const perfs = filteredPerfsForDate(key);
		if (!perfs.length) {
			return null;
		}
		// Compare effective display prices rather than raw per-ticket prices.
		// Kids Week rows are multiplied by 2 x partsCount (one adult + one child
		// per part); all other rows are multiplied by partsCount only.
		// This ensures a KW price that is nominally cheaper than the OLT price
		// does not incorrectly win as the day minimum — the displayed combined
		// price is what the user will actually pay.
		let min = null;
		perfs.forEach((p) => {
			if (isValidPrice(p.min)) {
				const isKids = (p.labels || []).some((l) => /kids/i.test(String(l || '')));
				const effective = p.min * partsCount * (isKids ? 2 : 1);
				min = (min === null) ? effective : Math.min(min, effective);
			}
		});
		return min;
	}
	function stripesForDay(key) {
		const rows = filteredPerfsForDate(key);
		if (!rows.length) {
			return [];
		}

		const nonCamp = rows.filter((p) => !isCampaignPerf(p)); // already clamped by isOfferAllowed
		const hasDisc = nonCamp.some(hasAnyDiscount); // any real discount (incl. OLT)
		const hasReband = nonCamp.some((p) => !isOlt(p) && !hasAnyDiscount(p)); // non-OLT reband

		const set = new Set();
		if ((hasDisc || hasReband) && !hideDiscountMessaging) {
			set.add('otd');
		}
		if (campaignUIOn && rows.some((r) => (r.labels || []).some((l) => /kids/i.test(String(l))))) {
			set.add('kw');
		}
		if (campaignUIOn && rows.some((r) => (r.labels || []).some((l) => /new[-\s]?year'?s?[-\s]?sale/i.test(String(l))))) {
			set.add('nys');
		}

		// Add the access stripe when the access_performances CPT has a performance on this date.
		if (accessDates[ key ] && Object.values(accessDates[ key ]).some((slugs) => slugs.length > 0)) {
			set.add('access');
		}

		return Array.from(set);
	}

	if (filterBtn && filterPop && filterListEl) {
		const parts = [];

		// decide how to display the OTD chip (Special Offer vs Exclusive Price)
		const { hasOffer, hasOfferDiscount } = promosPresent();

		if (hasOffer && !hideDiscountMessaging) {
			parts.push(`
				<label for="special-offer" class="farlo-ui-ticket-calendar__filter-item farlo-ui-ticket-calendar__filter-item--special-offer">
					<input id="special-offer" type="checkbox" value="special-offer" data-filter="offer">
					<span class="farlo-ui-ticket-calendar__filter-chip">${ hasOfferDiscount ? 'Special Offer' : 'Exclusive Price' }</span>
				</label>
			`);
		}

		labelUniverse.forEach((label) => {
			if (label === 'OLT') {
				return;
			}
			if (/tkts|on[-\s]?the[-\s]?day|special[-\s]?offer/i.test(String(label))) {
				return;
			}
			const displayText = label;
			const id = displayText.toLowerCase().replace(/\s+/g, '-');
			const mod = displayText.toLowerCase().replace(/\s+/g, '-');
			parts.push(`
				<label for="${ id }" class="farlo-ui-ticket-calendar__filter-item farlo-ui-ticket-calendar__filter-item--${ mod }">
					<input id="${ id }" type="checkbox" value="${ String(label).replace(/"/g, '&quot;') }" data-filter="label">
					<span class="farlo-ui-ticket-calendar__filter-chip">${ displayText }</span>
				</label>
			`);
		});

		['Matinee', 'Evening'].forEach((s) => {
			const id = s.toLowerCase().replace(/\s+/g, '-');
			parts.push(`
      <label for="${ id }" class="farlo-ui-ticket-calendar__filter-item">
        <input id="${ id }" type="checkbox" value="${ s }" data-filter="session">
        <span class="farlo-ui-ticket-calendar__filter-chip">${ s }</span>
      </label>
    `);
		});

		filterListEl.innerHTML = parts.join('');

		const toggle = () => {
			const wasInert = filterPop.hasAttribute('inert');
			if (wasInert) {
				filterPop.removeAttribute('inert');
			} else {
				filterPop.setAttribute('inert', '');
			}
			filterBtn.setAttribute('aria-expanded', wasInert ? 'true' : 'false');
		};
		filterBtn.addEventListener('click', toggle);
		document.addEventListener('click', (e) => {
			if (filterWrap && !filterWrap.contains(e.target)) {
				filterPop.setAttribute('inert', '');
				filterBtn.setAttribute('aria-expanded', 'false');
			}
		});

		filterListEl.addEventListener('change', (e) => {
			const el = e.target;
			if (!el || el.tagName !== 'INPUT') {
				return;
			}
			const type = el.dataset.filter;

			if (type === 'label') {
				const willTurnOn = el.checked;

				if (willTurnOn) {
					active.labels.add(el.value);
					// Jump to the first month that actually has matches
					jumpToFirstMatchingMonthForActiveFilters();
				} else {
					active.labels.delete(el.value);
				}
			}

			if (type === 'offer') {
				const willTurnOn = el.checked;
				const targetMode = hasOfferDiscount ? 'special' : 'exclusive';
				active.offerFilter = willTurnOn ? targetMode : null;
				if (willTurnOn) {
					jumpToFirstMatchingMonthForActiveFilters();
				}
			}

			if (type === 'session') {
				el.checked ? active.sessions.add(el.value) : active.sessions.delete(el.value);
			}

			filterClear.disabled = !anyActive();
			renderMonth(viewYear, viewMonth);
		});

		filterClear.addEventListener('click', () => {
			active.labels.clear();
			active.sessions.clear();
			active.offerFilter = null;
			filterListEl.querySelectorAll('input[type="checkbox"]').forEach((i) => {
				i.checked = false;
			});
			filterClear.disabled = true;
			renderMonth(viewYear, viewMonth);
		});
	}

	function updateNavState() {
		const minIdx = minDate ? monthIndex(minDate.getFullYear(), minDate.getMonth()) : null;
		const maxIdx = maxDate ? monthIndex(maxDate.getFullYear(), maxDate.getMonth()) : null;
		const curr = monthIndex(viewYear, viewMonth);
		const atMin = (minIdx !== null) && (curr <= minIdx);
		const atMax = (maxIdx !== null) && (curr >= maxIdx);
		prevBtn.disabled = atMin; nextBtn.disabled = atMax;
		prevBtn.classList.toggle('is-disabled', atMin);
		nextBtn.classList.toggle('is-disabled', atMax);
		prevBtn.setAttribute('aria-disabled', atMin ? 'true' : 'false');
		nextBtn.setAttribute('aria-disabled', atMax ? 'true' : 'false');
	}

	function renderMonth(y, m) {
		headerEl.textContent = ymToLabel(y, m);
		daysRoot.innerHTML = '';
		if (detailsRow) {
			detailsRow.remove();
			detailsRow = null;
		}
		activeDateKey = null;

		const first = firstDow(y, m),
			days = daysInMon(y, m);
		let week = document.createElement('div'); week.className = 'farlo-ui-ticket-calendar__week'; daysRoot.appendChild(week);

		for (let i = 1; i <= first; i ++) {
			const empty = document.createElement('div'); empty.className = 'farlo-ui-ticket-calendar__day farlo-ui-ticket-calendar__day--empty'; week.appendChild(empty);
		}

		let col = first + 1;
		for (let d = 1; d <= days; d ++) {
			if (col > 7) {
				col = 1; week = document.createElement('div'); week.className = 'farlo-ui-ticket-calendar__week'; daysRoot.appendChild(week);
			}

			const key = dateKey(y, m, d);
			const has = filteredHasDate(key);
			const from = has ? filteredMinForDate(key) : null;
			const stripes = has ? (stripesForDay(key) || []) : [];
			const stripesHTML = stripes.length
				? stripes.map((t) => `<span class="farlo-ui-ticket-calendar__day-stripe farlo-ui-ticket-calendar__day-stripe--${ t }" aria-hidden="true"></span>`).join('')
				: `<span class="farlo-ui-ticket-calendar__day-stripe farlo-ui-ticket-calendar__day-stripe--none" aria-hidden="true"></span>`;

			const btn = document.createElement('button');
			btn.type = 'button';
			btn.className = 'farlo-ui-ticket-calendar__day' + (has ? ' farlo-ui-ticket-calendar__day--has' : ' farlo-ui-ticket-calendar__day--disabled');
			btn.dataset.date = key;
			btn.innerHTML = `
                <span class="farlo-ui-ticket-calendar__stripes">${ stripesHTML }</span>
                <span class="farlo-ui-ticket-calendar__day-number">${ d }</span>
                ${ isValidPrice(from) ? `<span class="farlo-ui-ticket-calendar__day-price">${ formatMoney(from, currency) }</span>` : '' }
            `;
			if (!has) {
				btn.disabled = true;
				btn.setAttribute('aria-disabled', 'true');
			} else {
				btn.setAttribute('aria-label', 'Performances on ' + key);
			}
			week.appendChild(btn);
			col ++;
		}
		updateNavState();
	}

	function formatTime12h(time24) {
		const [hh, mm] = (time24 || '').split(':').map((n) => parseInt(n, 10) || 0);
		if (isNaN(hh)) {
			return '';
		}
		const date = new Date();
		date.setHours(hh, mm);
		return date
			.toLocaleTimeString('en-GB', {
				hour: 'numeric',
				minute: '2-digit',
				hour12: true
			})
			.replace(':00', '') // remove ":00" if exact hour
			.replace(' ', '')
			.toLowerCase(); // e.g. "2:30pm"
	}

	function jumpToFirstMatchingMonthForActiveFilters() {
		const candidates = perfsAll.filter((p) => {
			if (!p.date || (p.avail || 0) <= 0) {
				return false;
			}
			if (isPastPerf(p)) {
				return false;
			}
			if (!isOfferAllowed(p)) {
				return false;
			}
			return matchFilters(p);
		});

		if (!candidates.length) {
			return; // nothing to jump to
		}

		// Sort by date+time so we get the earliest matching perf
		candidates.sort((a, b) => {
			const aKey = `${ a.date || '' }T${ a.time || '' }`;
			const bKey = `${ b.date || '' }T${ b.time || '' }`;
			return aKey.localeCompare(bKey);
		});

		const first = candidates[ 0 ];
		const d = parseYMD(first.date);
		if (!d) {
			return;
		}

		viewYear = d.getFullYear();
		viewMonth = d.getMonth();
	}

	function buildGroupedHtmlForDate(key) {
		const perfs = filteredPerfsForDate(key);
		if (!perfs.length) {
			return '<p class="farlo-ui-ticket-calendar__empty">No performances matching your filters.</p>';
		}

		const money = (v) => (typeof v === 'number' && isFinite(v) && v > 0) ? formatMoney(v, currency) : '';
		const timeHtml = (p, show) =>
			show ? `<time class="farlo-ui-ticket-calendar__time">${ formatTime12h(p.time) }</time>`
				: '<span class="farlo-ui-ticket-calendar__time farlo-ui-ticket-calendar__time--placeholder" aria-hidden="true"></span>';

		const otherSeatsBadgeHtml = (show) =>
			show
				? `<span class="farlo-ui-ticket-calendar__badge farlo-ui-ticket-calendar__badge--other-seats">View other available seats</span>`
				: '';

		// --- split & decide like big calendar ---
		const nonCampaign = perfs.filter((p) => !isCampaignPerf(p));
		const oltRows = nonCampaign.filter(isOlt);
		const discRows = nonCampaign.filter((p) => hasAnyDiscount(p)); // includes discounted OLT
		const fullOlt = oltRows.filter((p) => !hasAnyDiscount(p)); // OLT at full price
		const undiscNonOlt = nonCampaign.filter((p) => !isOlt(p) && !hasAnyDiscount(p));

		// dedupe by time, prefer OLT then lower min
		const betterOf = (a, b) => {
			const aO = isOlt(a),
				bO = isOlt(b);
			if (bO && !aO) {
				return b;
			}
			if (aO && !bO) {
				return a;
			}
			const aMin = (typeof a.min === 'number' ? a.min : Infinity);
			const bMin = (typeof b.min === 'number' ? b.min : Infinity);
			return bMin < aMin ? b : a;
		};
		const dedupeByTime = (arr) => {
			const m = new Map();
			arr.forEach((p) => {
				const k = p.time;
				m.set(k, m.has(k) ? betterOf(m.get(k), p) : p);
			});
			return [...m.values()];
		};

		let offerRows, displayFullRows;
		if (discRows.length) {
			// SPECIAL OFFER: any discounted row (incl. OLT). Show full-price OLT separately.
			offerRows = dedupeByTime(discRows);
			displayFullRows = fullOlt;
		} else {
			// EXCLUSIVE PRICE: no discounts → re-band non-OLT only
			offerRows = dedupeByTime(undiscNonOlt);
			displayFullRows = fullOlt;
		}

		const kwRows = perfs.filter((r) => (r.labels || []).some((l) => /kids/i.test(String(l))));
		const nysRows = perfs.filter((r) => (r.labels || []).some((l) => /new\s*year'?s?\s*sale/i.test(String(l))));

		const offerBestPct = (() => {
			let best = null;
			offerRows.forEach((r) => {
				const p = r; if (hasAnyDiscount(p)) {
					best = best === null ? (p.savePct ?? p.discount_pct ?? p.discountPercent ?? p.savingPercent) : Math.max(best, (p.savePct ?? p.discount_pct ?? p.discountPercent ?? p.savingPercent));
				}
			});
			return best;
		})();

		const offerTitle = hideDiscountMessaging
			? ''
			: ((offerRows.length && !offerBestPct) ? 'Exclusive Price' : 'Special Offer');
		const offerNote = hideDiscountMessaging ? '' : (offerTitle === 'Exclusive Price' ? exclusiveOfferNote : specialOfferNote);

		const _esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ c ]));
		// Cheapest price across all options for analytics
		const _allMins = [...offerRows, ...displayFullRows].map((p) => Number(p.min)).filter((v) => v > 0);
		const _cheapest = _allMins.length ? Math.min(..._allMins) : 0;

		const renderSection = (title, rows, { mod = '', showPct = false, note = '', showOtherSeatsBadge = false } = {}) => {
			if (!rows.length) {
				return '';
			}
			const bestPct = showPct ? (offerBestPct || null) : null;
			const sectionIsOffer = mod === 'farlo-ui-ticket-calendar__section--offer';

			const byTime = rows.reduce((acc, p) => {
				(acc[ p.time ] ||= []).push(p); return acc;
			}, {});
			const listHtml = Object.values(byTime).map((group) =>
				group.slice(0, 1).map((p, i) => {
					const locked = isCampaignPerf(p) && !canBookNow();
					const past = isPastPerf(p);
					const pMin = (p.min !== null && p.min !== undefined) ? Number(p.min) : null;
					const isCheapest = (pMin !== null && _cheapest > 0 && Math.abs(pMin - _cheapest) < 0.01);
					const disc = p.savePct ?? p.discount_pct ?? p.discountPercent ?? p.savingPercent ?? 0;
					const dataAttrs = `data-event="click_price" data-id="${ payload.showId || '' }" data-name="${ _esc(payload.showName || '') }" data-offer="${ sectionIsOffer }" data-cheapest="${ isCheapest }" data-offer-perc="${ sectionIsOffer ? (parseInt(disc, 10) || 0) : 0 }" data-price="${ pMin !== null ? pMin.toFixed(2) : '' }" data-source="${ _esc(p.source || '') }"`;
					let rightHtml;

					const _isKidsPerf = (p.labels || []).some((l) => /kids/i.test(String(l)));
					const _campaignPrefix = _isKidsPerf ? 'Adult + Child' : 'Exclusive prices';
					// Multi-part shows require a ticket per part; Kids Week also doubles
					// for the mandatory adult + child pair.
					const _displayMin = p.min !== null && p.min !== undefined
						? Number(p.min) * partsCount * (_isKidsPerf ? 2 : 1)
						: p.min;

					if (locked) {
						rightHtml = `<div class="farlo-ui-ticket-calendar__from farlo-ui-ticket-calendar__from--locked">${ onSaleMsg() }</div>`;
					} else if (past) {
						rightHtml = `${ money(_displayMin) ? `<div class="farlo-ui-ticket-calendar__from">from <strong>${ money(_displayMin) }</strong></div>` : '' }
                       <button class="farlo-ui-ticket-calendar__book button button--book button--disabled" type="button" disabled aria-disabled="true">Unavailable</button>`;
					} else {
						rightHtml = `${ money(_displayMin) ? `<div class="farlo-ui-ticket-calendar__from">${ isCampaignPerf(p) ? _campaignPrefix : '' } from <strong>${ money(_displayMin) }</strong></div>` : '' }
              	 		<a class="farlo-ui-ticket-calendar__book button button--book" href="${ p.bookUrl || '#' }" ${ dataAttrs }>Book Tickets</a>`;
					}
					return `
          <div class="farlo-ui-ticket-calendar__option-row${ mod === 'farlo-ui-ticket-calendar__section--offer' ? ' farlo-ui-ticket-calendar__option-row--offer' : '' }">
            <div class="farlo-ui-ticket-calendar__option-left">
              <span class="farlo-ui-ticket-calendar__slot-time-group">
                ${ timeHtml(p, i === 0) }${ i === 0 ? accessIconsForSlot(key, p.time) : '' }
              </span>
              ${ (showPct && bestPct && !hideDiscountMessaging) ? `<span class="farlo-ui-ticket-calendar__badge farlo-ui-ticket-calendar__badge--save">up to ${ bestPct }% off</span>` : '' }
              ${ (!showPct && showOtherSeatsBadge && i === 0) ? otherSeatsBadgeHtml(true) : '' }
            </div>
            <div class="farlo-ui-ticket-calendar__option-right">${ rightHtml }</div>
          </div>`;
				}).join('')
			).join('');

			return `
      <div class="farlo-ui-ticket-calendar__section ${ mod }">
        ${ title ? `<h4 class="farlo-ui-ticket-calendar__section-title">${ title }</h4>` : '' }
        ${ listHtml }
        ${ note ? `<p class="farlo-ui-ticket-calendar__offer-note">${ note }</p>` : '' }
      </div>`;
		};

		return (
			(campaignUIOn ? renderSection('Kids Week', kwRows, { mod: 'farlo-ui-ticket-calendar__section--kw' }) : '') +
			(campaignUIOn ? renderSection('New Year Sale', nysRows, { mod: 'farlo-ui-ticket-calendar__section--nys' }) : '') +
			renderSection(offerTitle, offerRows, { mod: 'farlo-ui-ticket-calendar__section--offer', showPct: true, note: offerNote }) +
			renderSection('', displayFullRows, { mod: 'farlo-ui-ticket-calendar__section--full', showOtherSeatsBadge: true })
		);
	}

	function closeModal() {
		if (!modal) {
			return;
		}
		modal.hidden = true;
		document.documentElement.classList.remove('is-modal-open');
	}

	function openModalFor(key) {
		if (!modal) {
			console.error('Modal HTML element not found on the page.');
			return;
		}
		const d = parseYMD(key);
		if (modalTitle) {
			const w = d.toLocaleString(undefined, { weekday: 'short' });
			const day = d.toLocaleString(undefined, { day: 'numeric' });
			// Some en-GB browsers return "Sept" for a short month. Keep popup
			// headings consistent with the three-letter date labels used elsewhere.
			const month = d.toLocaleString(undefined, { month: 'short' }).slice(0, 3);
			modalTitle.innerHTML = `${ w } ${ day } ${ month }`;
		}
		const groupedHtml = buildGroupedHtmlForDate(key);
		if (modalContent) {
			modalContent.innerHTML = `<div class="farlo-ui-ticket-calendar__details-inner">${ groupedHtml }</div>`;
		}
		modal.hidden = false;
		document.documentElement.classList.add('is-modal-open');
	}

	function renderDetailsFor(key, weekEl) {
		if (activeDateKey === key && detailsRow) {
			detailsRow.remove();
			detailsRow = null;
			activeDateKey = null;
			daysRoot.querySelectorAll('.farlo-ui-ticket-calendar__day--active').forEach((el) => el.classList.remove('farlo-ui-ticket-calendar__day--active'));
			return;
		}

		activeDateKey = key;
		daysRoot.querySelectorAll('.farlo-ui-ticket-calendar__day--active').forEach((el) => el.classList.remove('farlo-ui-ticket-calendar__day--active'));
		const activeBtn = daysRoot.querySelector(`.farlo-ui-ticket-calendar__day[data-date="${ key }"]`);
		if (activeBtn) {
			activeBtn.classList.add('farlo-ui-ticket-calendar__day--active');
		}

		const row = ensureDetailsRow();
		const groupedHtml = buildGroupedHtmlForDate(key);
		row.innerHTML = `<div class="farlo-ui-ticket-calendar__details-inner">${ groupedHtml }</div>`;
		weekEl.insertAdjacentElement('afterend', row);
	}

	const legendEl = calendar.querySelector('[data-legend]');
	const legendNote = calendar.querySelector('[data-legend-note]');

	function promosPresent() {
		let hasOffer = false; // any non-campaign offer at all
		let hasOfferDiscount = false; // any of those has a real discount
		let hasKw = false,
			hasNys = false;

		const list = (payload.performances || [])
			.filter((p) => (p.avail || 0) > 0)
			.filter(isOfferAllowed);

		for (const p of list) {
			if (!isCampaignPerf(p)) {
				if (hasAnyDiscount(p)) {
					hasOffer = true;
					hasOfferDiscount = true;
				} else if (!isOlt(p)) {
					// re-banded non-OLT with no discount → exclusive
					hasOffer = true;
				}
			}
			if (campaignUIOn) {
				const labs = (p.pagesLabels || p.labels || []).map((l) => String(l).toLowerCase());
				if (labs.some((l) => /kids/.test(l))) {
					hasKw = true;
				}
				if (labs.some((l) => /new[-\s]?year'?s?[-\s]?sale/.test(l))) {
					hasNys = true;
				}
			}
		}
		return { hasOffer, hasOfferDiscount, hasKw, hasNys };
	}

	function buildLegend() {
		if (!legendEl) {
			return;
		}
		const { hasOffer, hasOfferDiscount, hasKw, hasNys } = promosPresent();
		const items = [];
		if (hasOffer && !hideDiscountMessaging) {
			items.push(
				`<span class="farlo-ui-ticket-calendar__legend-item">
				<span class="farlo-ui-ticket-calendar__legend-dot farlo-ui-ticket-calendar__legend-dot--otd ${ hasOfferDiscount ? 'farlo-ui-ticket-calendar__legend-dot--otd-offer' : '' }" aria-hidden="true"></span>
				<span class="farlo-ui-ticket-calendar__legend-label">${ hasOfferDiscount ? 'Special Offer' : 'Exclusive Price' }</span>
				</span>`
			);
		}
		if (hasKw) {
			items.push(`<span class="farlo-ui-ticket-calendar__legend-item"><span class="farlo-ui-ticket-calendar__legend-dot farlo-ui-ticket-calendar__legend-dot--kw" aria-hidden="true"></span><span class="farlo-ui-ticket-calendar__legend-label">Kids Week</span></span>`);
		}
		if (hasNys) {
			items.push(`<span class="farlo-ui-ticket-calendar__legend-item"><span class="farlo-ui-ticket-calendar__legend-dot farlo-ui-ticket-calendar__legend-dot--nys" aria-hidden="true"></span><span class="farlo-ui-ticket-calendar__legend-label">New Year Sale</span></span>`);
		}

		// Show the access legend item when at least one future access performance date exists.
		const hasAccess = Object.keys(accessDates).some((k) => {
			const entry = accessDates[ k ];
			return entry && typeof entry === 'object' &&
				Object.values(entry).some((slugs) => Array.isArray(slugs) && slugs.length > 0);
		});

		if (hasAccess) {
			items.push(
				`<span class="farlo-ui-ticket-calendar__legend-item"><span class="farlo-ui-ticket-calendar__legend-dot farlo-ui-ticket-calendar__legend-dot--access" aria-hidden="true"></span><span class="farlo-ui-ticket-calendar__legend-label">Access Performance</span></span>`
			);
		}

		if (items.length) {
			legendEl.innerHTML = items.join('');
			legendEl.removeAttribute('aria-hidden');
			legendNote?.removeAttribute('aria-hidden');
		} else {
			legendEl.innerHTML = '';
			legendEl.setAttribute('aria-hidden', 'true');
			legendNote?.setAttribute('aria-hidden', 'true');
		}
	}

	daysRoot.addEventListener('click', (e) => {
		const btn = e.target.closest('.farlo-ui-ticket-calendar__day--has');
		if (!btn) {
			return;
		}

		const key = btn.dataset.date;
		const perfs = filteredPerfsForDate(key);

		const hasOffer = perfs.some((p) =>
			!isCampaignPerf(p) && (
				hasAnyDiscount(p) ||
				(!isOlt(p) && isOfferAllowed(p))
			)

		);
		const hasCampaign = perfs.some((p) => isCampaignPerf(p));

		// Always use the modal for Offers or Campaigns (whether locked or open)
		if (hasOffer || hasCampaign) {
			openModalFor(key);
		} else {
			renderDetailsFor(key, btn.closest('.farlo-ui-ticket-calendar__week'));
		}
	});

	prevBtn.addEventListener('click', () => {
		if (prevBtn.disabled) {
			return;
		}
		viewMonth--; if (viewMonth < 0) {
			viewMonth = 11; viewYear--;
		}
		renderMonth(viewYear, viewMonth);
	});

	nextBtn.addEventListener('click', () => {
		if (nextBtn.disabled) {
			return;
		}
		viewMonth ++; if (viewMonth > 11) {
			viewMonth = 0; viewYear ++;
		}
		renderMonth(viewYear, viewMonth);
	});

	if (modal) {
		modalClose.forEach((btn) => btn.addEventListener('click', closeModal));
		modal.addEventListener('click', (e) => {
			if (e.target === modal) {
				closeModal();
			}
		});
	}

	renderMonth(viewYear, viewMonth);

	buildLegend();
}
